import React, { useEffect, useMemo, useState } from 'react';
import {
  Plus, Wallet, AlertTriangle, ArrowDownCircle, ArrowUpCircle,
  Scale, Building2, ShieldCheck, Download, Search, Pencil, Trash2,
} from 'lucide-react';
import {
  Card, CardHeader, CardBody, Button, Input, Select, Label, Badge,
  EmptyState, Modal, Textarea, KPI, Alert, ConfirmDeleteModal,
  SkeletonListRows,
} from '../components/ui';
import FirstVisitTips from '../components/onboarding/FirstVisitTips';
import { useFirm } from '../contexts/FirmContext';
import {
  listTrustAccounts, createTrustAccount, updateTrustAccount, deleteTrustAccount,
  listTrustTransactions, createTrustTransaction,
  listTrustReconciliations, createTrustReconciliation,
  listContacts, listMatters,
} from '../lib/db';
import { fullName, formatMoney, formatDate, classNames } from '../lib/formatters';
import { PlanGate } from '../contexts/PlanContext';
import { useActivityLogger } from '../lib/useActivityLogger';
import { withTimeout } from '../lib/fetchWithTimeout';
import toast from '../lib/toast';

const TX_TYPES = [
  { value: 'deposit',              label: 'Deposit',            sign: +1, icon: ArrowDownCircle, tone: 'green' },
  { value: 'withdrawal',           label: 'Withdrawal',         sign: -1, icon: ArrowUpCircle,   tone: 'red' },
  { value: 'transfer_to_operating',label: 'Transfer to Office', sign: -1, icon: ArrowUpCircle,   tone: 'amber' },
  { value: 'transfer_to_client',   label: 'Transfer to Client', sign: +1, icon: ArrowDownCircle, tone: 'blue' },
  { value: 'adjustment',           label: 'Adjustment / Fee',   sign: -1, icon: ArrowUpCircle,   tone: 'indigo' },
  { value: 'refund',               label: 'Refund',             sign: +1, icon: ArrowDownCircle, tone: 'slate' },
];

const LOW_BALANCE_THRESHOLD = 500; // retainer replenishment alert below $500

function TrustPageInner() {
  const { firmId, refreshSignal } = useFirm();
  const log = useActivityLogger();
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [reconciliations, setReconciliations] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [matters, setMatters] = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [view, setView] = useState('ledger'); // ledger | reconcile
  const [selectedAccount, setSelectedAccount] = useState('');
  const [q, setQ] = useState('');
  const [showNewAccount, setShowNewAccount] = useState(false);
  const [showNewTx, setShowNewTx] = useState(false);
  const [showReconcile, setShowReconcile] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);   // account object being edited
  const [deletingAccount, setDeletingAccount] = useState(null); // account object pending delete confirm
  const [deleteLoading, setDeleteLoading] = useState(false);

  const refresh = async () => {
    if (!firmId) { setLoading(false); return; }
    setFetchError(null);
    try {
      const [a, t, r, c, m] = await withTimeout(Promise.all([
        listTrustAccounts(firmId),
        listTrustTransactions(firmId),
        listTrustReconciliations(firmId),
        listContacts(firmId, 'client'),
        listMatters(firmId),
      ]), 20000);
      setAccounts(a.data || []);
      setTransactions(t.data || []);
      setReconciliations(r.data || []);
      setContacts(c.data || []);
      setMatters(m.data || []);
    } catch (err) {
      setFetchError(err?.message || 'Failed to load trust data. Please retry.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, [firmId, refreshSignal]); // eslint-disable-line

  // Per-client balances scoped to selected account (or all)
  const clientLedgers = useMemo(() => {
    const rows = transactions.filter((tx) =>
      !selectedAccount || tx.trust_account_id === selectedAccount,
    );
    const byClient = new Map();
    for (const tx of rows) {
      if (!tx.client_id) continue;
      const type = TX_TYPES.find((t) => t.value === tx.transaction_type);
      const sign = type?.sign ?? (tx.transaction_type === 'deposit' ? 1 : -1);
      const delta = sign * Number(tx.amount || 0);
      const prev = byClient.get(tx.client_id) || {
        client: tx.client, total: 0, count: 0, lastActivity: null,
      };
      prev.total += delta;
      prev.count += 1;
      const d = new Date(tx.transaction_date);
      if (!prev.lastActivity || d > prev.lastActivity) prev.lastActivity = d;
      byClient.set(tx.client_id, prev);
    }
    return Array.from(byClient.entries())
      .map(([id, v]) => ({ clientId: id, ...v }))
      .filter((r) => {
        if (!q) return true;
        return fullName(r.client)?.toLowerCase().includes(q.toLowerCase());
      })
      .sort((a, b) => b.total - a.total);
  }, [transactions, selectedAccount, q]);

  // Total ledger balance (per-account or overall)
  const totalLedgerBalance = useMemo(
    () => clientLedgers.reduce((s, r) => s + r.total, 0),
    [clientLedgers],
  );

  // Account-level rollups
  const accountTotals = useMemo(() => {
    return accounts.map((acc) => {
      const txs = transactions.filter((tx) => tx.trust_account_id === acc.id);
      let total = 0;
      const clients = new Set();
      for (const tx of txs) {
        const sign = TX_TYPES.find((t) => t.value === tx.transaction_type)?.sign
          ?? (tx.transaction_type === 'deposit' ? 1 : -1);
        total += sign * Number(tx.amount || 0);
        if (tx.client_id) clients.add(tx.client_id);
      }
      return { ...acc, balance: total, clientCount: clients.size, txCount: txs.length };
    });
  }, [accounts, transactions]);

  const lowBalanceClients = clientLedgers.filter((r) => r.total > 0 && r.total < LOW_BALANCE_THRESHOLD);
  const negativeBalanceClients = clientLedgers.filter((r) => r.total < 0);

  const exportCsv = () => {
    const rows = [['Date', 'Type', 'Client', 'Matter', 'Account', 'Amount', 'Reference', 'Description']];
    for (const tx of transactions) {
      rows.push([
        tx.transaction_date,
        tx.transaction_type,
        fullName(tx.client) || '',
        tx.matter?.matter_number || '',
        tx.account?.name || '',
        tx.amount,
        tx.reference || '',
        (tx.description || '').replace(/[\n,]/g, ' '),
      ]);
    }
    const csv = rows.map((r) => r.map((c) => `"${String(c ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `trust-ledger-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Wallet size={22} /> Client Accounting
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">Loading…</p>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <KPI key={i} label="" value="" loading icon={Wallet} tone="indigo" />
          ))}
        </div>
        <Card><CardBody><SkeletonListRows rows={5} /></CardBody></Card>
      </div>
    );
  }
  if (fetchError) return (
    <div className="flex flex-col items-center justify-center py-24 gap-4 text-center px-4">
      <p className="text-sm text-red-600 dark:text-red-400 max-w-sm">{fetchError}</p>
      <button onClick={refresh} className="px-4 py-2 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-medium transition">Retry</button>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Wallet size={22} /> Client Accounting
          </h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
            Client funds ledger · {accounts.length} account{accounts.length === 1 ? '' : 's'}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="ghost" icon={Download} onClick={exportCsv}>Export CSV</Button>
          <Button variant="secondary" icon={ShieldCheck} onClick={() => setShowReconcile(true)}>
            Reconcile
          </Button>
          <Button variant="secondary" icon={Building2} onClick={() => setShowNewAccount(true)}>
            New account
          </Button>
          <Button icon={Plus} onClick={() => setShowNewTx(true)} disabled={!accounts.length}>
            New transaction
          </Button>
        </div>
      </div>

      <FirstVisitTips
        pageKey="trust"
        title="Client funds — handle with care"
        tips={[
          'Every transaction is per-client and per-matter. You can\'t accidentally pay one client\'s costs from another client\'s retainer.',
          'A negative client ledger balance is a bar-rules violation. The Alerts section above flags any in red.',
          'Most jurisdictions require monthly three-way reconciliation — bank statement vs. book balance vs. the sum of all client ledgers. Use the Reconcile button when your bank statement arrives.',
        ]}
      />

      {!accounts.length && (
        <Alert tone="warning" title="No client accounts yet">
          Create your firm's client account first. Every client deposit must be tied to a specific client account.
        </Alert>
      )}

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI label="Total client funds"  value={formatMoney(totalLedgerBalance)} icon={Wallet}    tone="indigo" />
        <KPI label="Clients with funds"  value={clientLedgers.filter((r) => r.total > 0).length} icon={Scale} tone="blue" />
        <KPI label="Low-balance alerts"  value={lowBalanceClients.length}        icon={AlertTriangle} tone="amber" />
        <KPI label="Negative balances"   value={negativeBalanceClients.length}   icon={AlertTriangle} tone="red" />
      </div>

      {/* Alerts */}
      {negativeBalanceClients.length > 0 && (
        <Alert tone="error" title="Negative client account balances detected">
          {negativeBalanceClients.length} client ledger{negativeBalanceClients.length === 1 ? '' : 's'} show a negative balance. This is a professional conduct violation — investigate immediately.
        </Alert>
      )}
      {lowBalanceClients.length > 0 && (
        <Alert tone="warning" title="Retainer replenishment recommended">
          {lowBalanceClients.length} client{lowBalanceClients.length === 1 ? '' : 's'} below {formatMoney(LOW_BALANCE_THRESHOLD)}. Request replenishment before further billable work.
        </Alert>
      )}

      {/* Tabs */}
      <div className="border-b border-slate-200 dark:border-slate-800 -mb-px">
        <div className="flex gap-4">
          {[
            { key: 'ledger',    label: 'Client Ledgers' },
            { key: 'accounts',  label: 'Client Accounts' },
            { key: 'tx',        label: 'All Transactions' },
            { key: 'reconcile', label: 'Reconciliations' },
          ].map((t) => (
            <button
              key={t.key}
              onClick={() => setView(t.key)}
              className={classNames(
                'px-1 py-3 text-sm border-b-2 transition',
                view === t.key
                  ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 font-medium'
                  : 'border-transparent text-slate-500 hover:text-slate-700 dark:hover:text-slate-200',
              )}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {view === 'ledger' && (
        <Card>
          <div className="flex flex-col sm:flex-row gap-2 p-4 border-b border-slate-100 dark:border-slate-800">
            <div className="relative flex-1">
              <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search clients…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select
              value={selectedAccount}
              onChange={(e) => setSelectedAccount(e.target.value)}
              options={[
                { value: '', label: 'All accounts' },
                ...accounts.map((a) => ({ value: a.id, label: a.name })),
              ]}
            />
          </div>
          {!clientLedgers.length ? (
            <EmptyState
              icon={Wallet}
              title="Track client funds separately"
              description="The trust ledger holds money that belongs to your clients — retainers, settlement proceeds, escrowed funds. Record your first deposit to begin per-client tracking. Required by the bar in most jurisdictions."
              action={<Button icon={Plus} onClick={() => setShowNewTx(true)} disabled={!accounts.length}>New transaction</Button>}
            />
          ) : (
            <>
              {/* Mobile cards */}
              <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                {clientLedgers.map((row) => {
                  const tone = row.total < 0 ? 'red' : row.total < LOW_BALANCE_THRESHOLD ? 'amber' : 'green';
                  const label = row.total < 0 ? 'Negative' : row.total < LOW_BALANCE_THRESHOLD ? 'Low' : 'Healthy';
                  return (
                    <li key={row.clientId} className="px-4 py-3 flex items-center justify-between gap-2">
                      <div>
                        <p className="font-medium text-sm">{fullName(row.client) || row.client?.organization || '—'}</p>
                        <p className="text-xs text-slate-500">{row.count} transactions · Last {row.lastActivity ? formatDate(row.lastActivity) : '—'}</p>
                        <Badge tone={tone}>{label}</Badge>
                      </div>
                      <p className={classNames('font-mono font-semibold text-sm', row.total < 0 ? 'text-red-600' : 'text-slate-900 dark:text-slate-100')}>{formatMoney(row.total)}</p>
                    </li>
                  );
                })}
                <li className="px-4 py-3 flex justify-between bg-slate-50 dark:bg-slate-800/40 font-semibold text-sm">
                  <span>Total</span>
                  <span className="font-mono">{formatMoney(totalLedgerBalance)}</span>
                </li>
              </ul>
              {/* Desktop table */}
              <div className="hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Client</th>
                    <th className="text-right px-6 py-2 font-medium">Balance</th>
                    <th className="text-right px-6 py-2 font-medium">Transactions</th>
                    <th className="text-left px-6 py-2 font-medium">Last activity</th>
                    <th className="text-left px-6 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {clientLedgers.map((row) => {
                    const tone = row.total < 0 ? 'red'
                      : row.total < LOW_BALANCE_THRESHOLD ? 'amber'
                      : 'green';
                    const label = row.total < 0 ? 'Negative'
                      : row.total < LOW_BALANCE_THRESHOLD ? 'Low'
                      : 'Healthy';
                    return (
                      <tr key={row.clientId}>
                        <td className="px-6 py-3 font-medium">{fullName(row.client) || row.client?.organization || '—'}</td>
                        <td className={classNames(
                          'px-6 py-3 text-right font-mono font-semibold',
                          row.total < 0 ? 'text-red-600' : 'text-slate-900 dark:text-slate-100',
                        )}>{formatMoney(row.total)}</td>
                        <td className="px-6 py-3 text-right text-slate-500">{row.count}</td>
                        <td className="px-6 py-3 text-slate-500">{row.lastActivity ? formatDate(row.lastActivity) : '—'}</td>
                        <td className="px-6 py-3"><Badge tone={tone}>{label}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-slate-50 dark:bg-slate-800/40 font-semibold">
                    <td className="px-6 py-3">Total</td>
                    <td className="px-6 py-3 text-right font-mono">{formatMoney(totalLedgerBalance)}</td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
              </div>
            </>
          )}
        </Card>
      )}

      {view === 'accounts' && (
        <Card>
          <CardBody className="p-0">
            {!accounts.length ? (
              <EmptyState
                icon={Building2}
                title="Add your trust account"
                description="Most jurisdictions require attorneys to hold client funds in a separate trust (IOLTA) bank account. Add yours here so deposits and withdrawals can be properly recorded and reconciled."
                action={<Button icon={Plus} onClick={() => setShowNewAccount(true)}>New account</Button>}
              />
            ) : (
              <>
                {/* Mobile cards */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {accountTotals.map((a) => (
                    <li key={a.id} className="px-4 py-3">
                      <div className="flex items-start justify-between gap-2">
                        <div>
                          <p className="font-medium text-sm">{a.name}</p>
                          <p className="text-xs text-slate-500">{a.bank_name || '—'} · ...{(a.account_last4 || '').slice(-4)}</p>
                          <Badge tone={a.is_iolta ? 'indigo' : 'slate'}>{a.is_iolta ? 'Pooled' : 'Non-pooled'}</Badge>
                          <p className="text-xs text-slate-400 mt-1">{a.clientCount} clients · {a.txCount} transactions</p>
                        </div>
                        <div className="text-right">
                          <p className="font-mono font-semibold text-sm">{formatMoney(a.balance)}</p>
                          <div className="flex items-center gap-1 mt-2 justify-end">
                            <button onClick={() => setEditingAccount(a)} className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600"><Pencil size={13} /></button>
                            <button onClick={() => setDeletingAccount(a)} className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Account</th>
                    <th className="text-left px-6 py-2 font-medium">Bank</th>
                    <th className="text-left px-6 py-2 font-medium">Type</th>
                    <th className="text-right px-6 py-2 font-medium">Ledger balance</th>
                    <th className="text-right px-6 py-2 font-medium">Clients</th>
                    <th className="text-right px-6 py-2 font-medium">Transactions</th>
                    <th className="px-6 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {accountTotals.map((a) => (
                    <tr key={a.id}>
                      <td className="px-6 py-3">
                        <p className="font-medium">{a.name}</p>
                        <p className="text-xs text-slate-500">...{(a.account_last4 || '').slice(-4)}</p>
                      </td>
                      <td className="px-6 py-3 text-slate-600 dark:text-slate-300">{a.bank_name || '—'}</td>
                      <td className="px-6 py-3">
                        <Badge tone={a.is_iolta ? 'indigo' : 'slate'}>
                          {a.is_iolta ? 'Pooled' : 'Non-pooled'}
                        </Badge>
                      </td>
                      <td className="px-6 py-3 text-right font-mono font-semibold">{formatMoney(a.balance)}</td>
                      <td className="px-6 py-3 text-right text-slate-500">{a.clientCount}</td>
                      <td className="px-6 py-3 text-right text-slate-500">{a.txCount}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            onClick={() => setEditingAccount(a)}
                            className="p-1.5 rounded hover:bg-slate-100 dark:hover:bg-slate-700 text-slate-400 hover:text-indigo-600 transition-colors"
                            title="Edit account"
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            onClick={() => setDeletingAccount(a)}
                            className="p-1.5 rounded hover:bg-red-50 dark:hover:bg-red-900/20 text-slate-400 hover:text-red-600 transition-colors"
                            title="Delete account"
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {view === 'tx' && (
        <Card>
          <CardBody className="p-0">
            {!transactions.length ? (
              <EmptyState icon={Scale} title="No transactions" />
            ) : (
              <>
                {/* Mobile cards */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {transactions.map((tx) => {
                    const type = TX_TYPES.find((t) => t.value === tx.transaction_type);
                    const sign = type?.sign ?? 1;
                    return (
                      <li key={tx.id} className="px-4 py-3 flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <Badge tone={type?.tone}>{type?.label || tx.transaction_type}</Badge>
                            <span className="text-xs text-slate-400">{formatDate(tx.transaction_date)}</span>
                          </div>
                          <p className="text-sm font-medium mt-1">{fullName(tx.client) || '—'}</p>
                          <p className="text-xs text-slate-500">{tx.matter?.matter_number ? `${tx.matter.matter_number} · ` : ''}{tx.account?.name || '—'}</p>
                          {tx.reference && <p className="text-xs font-mono text-slate-400">{tx.reference}</p>}
                        </div>
                        <p className={classNames('font-mono font-semibold text-sm flex-shrink-0', sign < 0 ? 'text-red-600' : 'text-green-600')}>
                          {sign < 0 ? '−' : '+'}{formatMoney(Math.abs(Number(tx.amount || 0)))}
                        </p>
                      </li>
                    );
                  })}
                </ul>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">Date</th>
                    <th className="text-left px-6 py-2 font-medium">Type</th>
                    <th className="text-left px-6 py-2 font-medium">Client · Case File</th>
                    <th className="text-left px-6 py-2 font-medium">Account</th>
                    <th className="text-left px-6 py-2 font-medium">Reference</th>
                    <th className="text-right px-6 py-2 font-medium">Amount</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {transactions.map((tx) => {
                    const type = TX_TYPES.find((t) => t.value === tx.transaction_type);
                    const sign = type?.sign ?? 1;
                    return (
                      <tr key={tx.id}>
                        <td className="px-6 py-3">{formatDate(tx.transaction_date)}</td>
                        <td className="px-6 py-3"><Badge tone={type?.tone}>{type?.label || tx.transaction_type}</Badge></td>
                        <td className="px-6 py-3">
                          <p>{fullName(tx.client) || '—'}</p>
                          <p className="text-xs text-slate-500">{tx.matter?.matter_number || ''} {tx.matter?.title}</p>
                        </td>
                        <td className="px-6 py-3 text-slate-500">{tx.account?.name || '—'}</td>
                        <td className="px-6 py-3 text-slate-500 font-mono text-xs">{tx.reference || '—'}</td>
                        <td className={classNames(
                          'px-6 py-3 text-right font-mono font-semibold',
                          sign < 0 ? 'text-red-600' : 'text-green-600',
                        )}>
                          {sign < 0 ? '−' : '+'}{formatMoney(Math.abs(Number(tx.amount || 0)))}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {view === 'reconcile' && (
        <Card>
          <CardHeader
            title="Reconciliation history"
            subtitle="Three-way reconciliation (bank · book · client ledger)"
            action={<Button variant="secondary" icon={ShieldCheck} onClick={() => setShowReconcile(true)}>New reconciliation</Button>}
          />
          <CardBody className="p-0">
            {!reconciliations.length ? (
              <EmptyState
                icon={ShieldCheck}
                title="No reconciliations on record"
                description="Most state bars require monthly three-way reconciliations. Record one now to stay compliant."
                action={<Button icon={Plus} onClick={() => setShowReconcile(true)}>Reconcile now</Button>}
              />
            ) : (
              <>
                {/* Mobile cards */}
                <ul className="sm:hidden divide-y divide-slate-100 dark:divide-slate-800">
                  {reconciliations.map((r) => {
                    const diff = Number(r.bank_balance || 0) - Number(r.ledger_balance || 0);
                    const balanced = Math.abs(diff) < 0.01;
                    return (
                      <li key={r.id} className="px-4 py-3">
                        <div className="flex items-start justify-between gap-2">
                          <div>
                            <p className="font-medium text-sm">{accounts.find((a) => a.id === r.trust_account_id)?.name || '—'}</p>
                            <p className="text-xs text-slate-500">As of {formatDate(r.reconciliation_date)}</p>
                            <Badge tone={balanced ? 'green' : 'red'}>{balanced ? 'Balanced' : 'Out of balance'}</Badge>
                          </div>
                          <div className="text-right text-xs text-slate-500 space-y-0.5">
                            <p>Bank: <span className="font-mono">{formatMoney(r.bank_balance)}</span></p>
                            <p>Book: <span className="font-mono">{formatMoney(r.ledger_balance)}</span></p>
                            <p>Ledger: <span className="font-mono">{formatMoney(r.client_ledger_total)}</span></p>
                            <p className={classNames('font-mono font-semibold', balanced ? 'text-green-600' : 'text-red-600')}>Diff: {formatMoney(diff)}</p>
                          </div>
                        </div>
                      </li>
                    );
                  })}
                </ul>
                {/* Desktop table */}
                <div className="hidden sm:block overflow-x-auto">
                <table className="w-full text-sm">
                <thead className="text-xs uppercase tracking-wide text-slate-500 bg-slate-50 dark:bg-slate-800/40">
                  <tr>
                    <th className="text-left px-6 py-2 font-medium">As of</th>
                    <th className="text-left px-6 py-2 font-medium">Account</th>
                    <th className="text-right px-6 py-2 font-medium">Bank</th>
                    <th className="text-right px-6 py-2 font-medium">Book</th>
                    <th className="text-right px-6 py-2 font-medium">Client ledger</th>
                    <th className="text-right px-6 py-2 font-medium">Diff</th>
                    <th className="text-left px-6 py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {reconciliations.map((r) => {
                    const diff = Number(r.bank_balance || 0) - Number(r.ledger_balance || 0);
                    return (
                      <tr key={r.id}>
                        <td className="px-6 py-3">{formatDate(r.reconciliation_date)}</td>
                        <td className="px-6 py-3">{accounts.find((a) => a.id === r.trust_account_id)?.name || '—'}</td>
                        <td className="px-6 py-3 text-right font-mono">{formatMoney(r.bank_balance)}</td>
                        <td className="px-6 py-3 text-right font-mono">{formatMoney(r.ledger_balance)}</td>
                        <td className="px-6 py-3 text-right font-mono">{formatMoney(r.client_ledger_total)}</td>
                        <td className={classNames(
                          'px-6 py-3 text-right font-mono',
                          Math.abs(diff) < 0.01 ? 'text-green-600' : 'text-red-600',
                        )}>{formatMoney(diff)}</td>
                        <td className="px-6 py-3">
                          <Badge tone={Math.abs(diff) < 0.01 ? 'green' : 'red'}>
                            {Math.abs(diff) < 0.01 ? 'Balanced' : 'Out of balance'}
                          </Badge>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      )}

      {/* Modals */}
      {showNewAccount && (
        <NewAccountModal
          open={showNewAccount}
          onClose={() => setShowNewAccount(false)}
          firmId={firmId}
          onCreated={(acc) => { setAccounts((prev) => [...prev, acc]); setShowNewAccount(false); }}
        />
      )}

      {editingAccount && (
        <EditAccountModal
          open={!!editingAccount}
          account={editingAccount}
          onClose={() => setEditingAccount(null)}
          onSaved={(updated) => {
            setAccounts((prev) => prev.map((a) => a.id === updated.id ? updated : a));
            setEditingAccount(null);
          }}
        />
      )}

      <ConfirmDeleteModal
        open={!!deletingAccount}
        onClose={() => setDeletingAccount(null)}
        loading={deleteLoading}
        title="Delete client account"
        description={<>Are you sure you want to delete <strong>{deletingAccount?.name}</strong>? All associated transaction records will also be removed.</>}
        onConfirm={async () => {
          setDeleteLoading(true);
          const { error } = await deleteTrustAccount(deletingAccount.id);
          setDeleteLoading(false);
          if (error) { toast.error(error.message); return; }
          setAccounts((prev) => prev.filter((a) => a.id !== deletingAccount.id));
          setDeletingAccount(null);
        }}
      />

      {showNewTx && (
        <NewTransactionModal
          open={showNewTx}
          onClose={() => setShowNewTx(false)}
          firmId={firmId}
          accounts={accounts}
          clients={contacts}
          matters={matters}
          onCreated={(tx) => {
            setTransactions((prev) => [tx, ...prev]);
            setShowNewTx(false);
            log('trust', tx.id, 'created', `Trust ${tx.transaction_type}: ${tx.amount}`);
          }}
        />
      )}

      {showReconcile && (
        <ReconcileModal
          open={showReconcile}
          onClose={() => setShowReconcile(false)}
          firmId={firmId}
          accounts={accountTotals}
          clientLedgerTotal={totalLedgerBalance}
          onCreated={(r) => { setReconciliations((prev) => [r, ...prev]); setShowReconcile(false); setView('reconcile'); }}
        />
      )}
    </div>
  );
}

// ---------------- Edit Account Modal -----------------
const EditAccountModal = ({ open, account, onClose, onSaved }) => {
  const [form, setForm] = useState({
    name:            account.name || '',
    bank_name:       account.bank_name || '',
    account_last4:   account.account_last4 || '',
    is_iolta:        account.is_iolta ?? true,
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name) return;
    setSaving(true);
    const { data, error } = await updateTrustAccount(account.id, {
      name:          form.name,
      bank_name:     form.bank_name,
      account_last4: form.account_last4,
      is_iolta:      form.is_iolta,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    onSaved(data);
  };

  return (
    <Modal open={open} onClose={onClose} title="Edit client account" footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} onClick={submit}>Save changes</Button>
      </>
    }>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label required>Account name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div>
          <Label>Bank name</Label>
          <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
        </div>
        <div>
          <Label>Last 4 digits</Label>
          <Input value={form.account_last4} onChange={(e) => setForm({ ...form, account_last4: e.target.value })} placeholder="e.g. 1234" maxLength={4} />
        </div>
        <div className="sm:col-span-2 flex items-center gap-2">
          <input
            type="checkbox"
            id="edit_is_iolta"
            checked={form.is_iolta}
            onChange={(e) => setForm({ ...form, is_iolta: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600"
          />
          <label htmlFor="edit_is_iolta" className="text-sm text-slate-700 dark:text-slate-300">
            Pooled client account
          </label>
        </div>
      </form>
    </Modal>
  );
};

// ---------------- New Account Modal -----------------
const NewAccountModal = ({ open, onClose, firmId, onCreated }) => {
  const [form, setForm] = useState({
    name: '', bank_name: '', account_last4: '',
    is_iolta: true, opening_balance: '',
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.name) return;
    setSaving(true);
    const payload = {
      name: form.name,
      bank_name: form.bank_name,
      account_last4: form.account_last4,
      is_iolta: form.is_iolta,
      ...(form.opening_balance ? { opening_balance: Number(form.opening_balance) } : {}),
    };
    const { data, error } = await createTrustAccount(firmId, payload);
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    onCreated(data);
  };

  return (
    <Modal open={open} onClose={onClose} title="New client account" footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} onClick={submit}>Create</Button>
      </>
    }>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <Label required>Account name</Label>
          <Input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Client Account — Main" />
        </div>
        <div>
          <Label>Bank name</Label>
          <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} />
        </div>
        <div>
          <Label>Last 4 digits</Label>
          <Input value={form.account_last4} onChange={(e) => setForm({ ...form, account_last4: e.target.value })} placeholder="e.g. 1234" maxLength={4} />
        </div>
        <div>
          <Label>Opening balance</Label>
          <Input type="number" min="0" step="0.01" value={form.opening_balance} onChange={(e) => setForm({ ...form, opening_balance: e.target.value })} placeholder="0.00" />
        </div>
        <div className="flex items-center gap-2 pt-5">
          <input
            type="checkbox"
            id="is_iolta"
            checked={form.is_iolta}
            onChange={(e) => setForm({ ...form, is_iolta: e.target.checked })}
            className="h-4 w-4 rounded border-slate-300 text-indigo-600"
          />
          <label htmlFor="is_iolta" className="text-sm text-slate-700 dark:text-slate-300">
            Pooled client account
          </label>
        </div>
      </form>
    </Modal>
  );
};

// ---------------- New Transaction Modal -----------------
const NewTransactionModal = ({ open, onClose, firmId, accounts, clients, matters, onCreated }) => {
  const [form, setForm] = useState({
    transaction_type: 'deposit',
    transaction_date: new Date().toISOString().slice(0, 10),
    amount: '',
    trust_account_id: accounts[0]?.id || '',
    client_id: '',
    matter_id: '',
    reference: '',
    description: '',
  });
  const [saving, setSaving] = useState(false);

  const clientMatters = matters.filter((m) => !form.client_id || m.client_id === form.client_id);

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.amount || !form.trust_account_id) return;
    setSaving(true);
    try {
      const payload = {
        transaction_type: form.transaction_type,
        transaction_date: form.transaction_date,
        amount:           Number(form.amount),
        trust_account_id: form.trust_account_id,
        client_id:        form.client_id  || null,
        matter_id:        form.matter_id  || null,
        reference:        form.reference  || null,
        description:      form.description || null,
      };
      const { data, error } = await createTrustTransaction(firmId, payload);
      if (error) { toast.error(error.message); return; }
      onCreated(data);
    } catch (err) {
      toast.error(err.message || 'Failed to record transaction.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose} title="New trust transaction" size="lg" footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} onClick={submit}>Record transaction</Button>
      </>
    }>
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label required>Type</Label>
          <Select required options={TX_TYPES.map((t) => ({ value: t.value, label: t.label }))}
            value={form.transaction_type}
            onChange={(e) => setForm({ ...form, transaction_type: e.target.value })} />
        </div>
        <div>
          <Label required>Date</Label>
          <Input type="date" required value={form.transaction_date}
            onChange={(e) => setForm({ ...form, transaction_date: e.target.value })} />
        </div>
        <div>
          <Label required>Amount</Label>
          <Input type="number" step="0.01" min="0" required value={form.amount}
            onChange={(e) => setForm({ ...form, amount: e.target.value })} placeholder="0.00" />
        </div>
        <div>
          <Label required>Account</Label>
          <Select required options={accounts.map((a) => ({ value: a.id, label: a.name }))}
            value={form.trust_account_id}
            onChange={(e) => setForm({ ...form, trust_account_id: e.target.value })} />
        </div>
        <div>
          <Label>Client</Label>
          <Select placeholder="Select…" options={clients.map((c) => ({ value: c.id, label: fullName(c) || c.organization }))}
            value={form.client_id}
            onChange={(e) => setForm({ ...form, client_id: e.target.value, matter_id: '' })} />
        </div>
        <div>
          <Label>Case File</Label>
          <Select placeholder="Select…" options={clientMatters.map((m) => ({ value: m.id, label: `${m.matter_number} — ${m.title}` }))}
            value={form.matter_id}
            onChange={(e) => setForm({ ...form, matter_id: e.target.value })} />
        </div>
        <div className="sm:col-span-2">
          <Label>Reference / check #</Label>
          <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })} placeholder="Cheque number, transfer ref, etc." />
        </div>
        <div className="sm:col-span-2">
          <Label>Memo</Label>
          <Textarea rows={2} value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })} />
        </div>
      </form>
    </Modal>
  );
};

// ---------------- Reconciliation Modal -----------------
const ReconcileModal = ({ open, onClose, firmId, accounts, clientLedgerTotal, onCreated }) => {
  const [form, setForm] = useState({
    trust_account_id: accounts[0]?.id || '',
    as_of_date: new Date().toISOString().slice(0, 10),
    bank_balance: '',
    notes: '',
  });
  const [saving, setSaving] = useState(false);

  const acc = accounts.find((a) => a.id === form.trust_account_id);
  const bookBalance = acc?.balance || 0;
  const bank = Number(form.bank_balance || 0);
  const bankVsBook = bank - bookBalance;
  const bookVsLedger = bookBalance - clientLedgerTotal;
  const isBalanced = Math.abs(bankVsBook) < 0.01 && Math.abs(bookVsLedger) < 0.01;

  const submit = async (e) => {
    e?.preventDefault();
    if (!form.trust_account_id || !form.bank_balance) return;
    setSaving(true);
    const { data, error } = await createTrustReconciliation(firmId, {
      trust_account_id:    form.trust_account_id,
      reconciliation_date: form.as_of_date,
      bank_balance:        bank,
      ledger_balance:      bookBalance,
      client_ledger_total: clientLedgerTotal,
      notes:               form.notes,
    });
    setSaving(false);
    if (error) { toast.error(error.message); return; }
    onCreated(data);
  };

  return (
    <Modal open={open} onClose={onClose} size="lg" title="Three-way reconciliation" footer={
      <>
        <Button variant="ghost" onClick={onClose}>Cancel</Button>
        <Button loading={saving} onClick={submit} disabled={!form.bank_balance || !form.trust_account_id}>
          Save reconciliation
        </Button>
      </>
    }>
      <form onSubmit={submit} className="space-y-4">
        <p className="text-sm text-slate-600 dark:text-slate-300">
          A three-way reconciliation compares the bank statement, your internal book balance,
          and the sum of all client ledgers. All three must match — any difference is a red flag.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label required>Account</Label>
            <Select required options={accounts.map((a) => ({ value: a.id, label: a.name }))}
              value={form.trust_account_id}
              onChange={(e) => setForm({ ...form, trust_account_id: e.target.value })} />
          </div>
          <div>
            <Label required>As of date</Label>
            <Input type="date" required value={form.as_of_date}
              onChange={(e) => setForm({ ...form, as_of_date: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Label required>Bank statement balance</Label>
            <Input type="number" step="0.01" required value={form.bank_balance}
              onChange={(e) => setForm({ ...form, bank_balance: e.target.value })} placeholder="Enter ending balance from bank statement" />
          </div>
        </div>

        <Card className="bg-slate-50 dark:bg-slate-800/40">
          <CardBody>
            <h4 className="text-sm font-semibold mb-3">Reconciliation preview</h4>
            <div className="space-y-2 text-sm">
              <Row3 label="Bank balance"        value={bank} />
              <Row3 label="Book balance (ours)" value={bookBalance} />
              <Row3 label="Sum of client ledgers" value={clientLedgerTotal} />
              <div className="h-px bg-slate-200 dark:bg-slate-700 my-2" />
              <Row3 label="Bank − Book"         value={bankVsBook} diff />
              <Row3 label="Book − Client ledgers" value={bookVsLedger} diff />
            </div>
            <div className="mt-4">
              {isBalanced ? (
                <Badge tone="green">Balanced — all three agree</Badge>
              ) : (
                <Badge tone="red">Out of balance — investigate differences</Badge>
              )}
            </div>
          </CardBody>
        </Card>

        <div>
          <Label>Notes</Label>
          <Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Outstanding checks, deposits in transit, reconciling items…" />
        </div>
      </form>
    </Modal>
  );
};

const Row3 = ({ label, value, diff }) => (
  <div className="flex items-center justify-between">
    <span className="text-slate-500 dark:text-slate-400">{label}</span>
    <span className={classNames(
      'font-mono font-semibold',
      diff && Math.abs(value) >= 0.01 ? 'text-red-600' : 'text-slate-900 dark:text-slate-100',
    )}>{formatMoney(value)}</span>
  </div>
);

export default function TrustPage() {
  return <PlanGate feature="trust"><TrustPageInner /></PlanGate>;
}
